/**
 * PlanosAssinatura — tela 8 do funil Premium: o paywall novo.
 *
 * Fonte: handoffAnuncios/PROMPT-claude-code.md § 8 e Monetizacao.dc.html (1h).
 * Os números do desenho são ilustrativos: TODO preço sai da loja.
 *
 * Nativo (RevenueCat, via ./lojaPlanos):
 *   • Anual primeiro e pré-selecionado; Mensal depois, como âncora;
 *   • Android BR: se o offering trouxer o parcelado (annual_installments), o
 *     card Anual ganha o segmentado "À vista · {total}" | "12x de {parcela}";
 *   • teste grátis só quando a LOJA diz que este usuário tem direito
 *     (iOS: checkTrialOrIntroductoryPriceEligibility = ELIGIBLE; Android:
 *     defaultOption.freePhase). A duração vem da loja, não é fixa;
 *   • "Economize N%" = anual/12 contra o mensal, com os preços reais;
 *   • barra fixa: "Cancele quando quiser…", CTA amarelo, link externo
 *     (quando `linkExterno.ativo`, sempre ABAIXO e menor que a compra na loja),
 *     texto legal obrigatório e Restaurar compras · Termos · Privacidade.
 *
 * Web: o checkout é o /assinar (só anual, preço de /pay/checkout/planos). A
 * tela mostra esse plano e o CTA leva para lá — sem RevenueCat.
 *
 * Compra concluída → onComprado({ pacote, customerInfo, emTeste }), com
 * emTeste = alguma entitlement ativa com periodType TRIAL. Restaurar compras
 * com assinatura ativa chama o mesmo onComprado (pacote null, restaurado true).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import haptics from '../../design/feedback/haptics';
import cx from '../../design/cx';
import Loader from '../../design/components/Loader/Loader';
import { useUser } from '../../contexts/UserContext';
import CodigoCriador from './CodigoCriador';
import { auth } from '../../firebaseConfig';
import { FEATURES, TIERS } from '../../config/planoConfig';
import { registrarMetrica } from '../monetizacao/anunciosApi';
import { registrarEventoCriador } from '../criador/dispositivo';
import PainelPremium from './PainelPremium';
import PremiumTopBar from './PremiumTopBar';
import {
  carregarPlanosDaLoja,
  carregarPlanoWeb,
  comprarPacote,
  compraCancelada,
  economiaPercentual,
  estaEmTeste,
  formatarPreco,
  lojaIndisponivel,
  restaurarCompras,
  temAssinaturaAtiva,
  textosDaLoja,
  URL_PRIVACIDADE,
  URL_TERMOS,
} from './lojaPlanos';
import vitaCoroa from '../../Assets/images/vita/vita-premium-coroa.webp';
import s from './PlanosAssinatura.module.scss';

const TEMPO_LIMITE_MS = 15000;
const ERRO_GENERICO = 'Não foi possível concluir a compra. Tente novamente.';

// "Sem anúncios" só é benefício se o planoConfig disser que o Premium não vê anúncio.
const PREMIUM_SEM_ANUNCIOS = FEATURES.ads?.[TIERS.PREMIUM] === false;

const Check = ({ tamanho = 16, traco = 3 }) => (
  <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={traco} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

function Beneficio({ children, sem = false }) {
  return (
    <li className={cx(s.beneficio, sem && s.beneficioSem)}>
      {sem ? <span className={s.beneficioTraco} aria-hidden="true" /> : <Check />}
      {children}
    </li>
  );
}

/** Texto legal da loja (renovação, preço após o teste, cancelamento). */
function textoLegal(plano, loja) {
  if (!plano) return '';
  const teste = plano.teste;
  if (plano.tipo === 'parcelado') {
    return [
      `${teste ? `Após ${teste.rotulo} grátis, serão` : 'Serão'} ${plano.parcelas} cobranças mensais de ${plano.parcelaTexto} (${plano.totalTexto} no total), com compromisso de ${plano.parcelas} meses.`,
      'Cancelar interrompe a renovação ao fim do compromisso; as parcelas restantes continuam sendo cobradas.',
      `A assinatura é renovada automaticamente se não for cancelada pelo menos 24 horas antes do fim do ${teste ? 'teste ou do ' : ''}período.`,
      `Gerencie ou cancele ${loja.ondeCancelar}.`,
    ].join(' ');
  }
  const periodo = plano.tipo === 'anual' ? 'ano' : 'mês';
  return [
    teste
      ? `Após ${teste.rotulo} grátis, será cobrado ${plano.totalTexto} por ${periodo}.`
      : `Cobrança de ${plano.totalTexto} por ${periodo}.`,
    `Sua assinatura é renovada automaticamente se não for cancelada pelo menos 24 horas antes do fim do ${teste ? 'teste ou do ' : ''}período.`,
    `Cancele a qualquer momento ${loja.ondeCancelar}, sem custo. O acesso continua até o fim do período atual.`,
  ].join(' ');
}

export default function PlanosAssinatura({ origem, onComprado, onFechar, onVoltar, linkExterno }) {
  const nativo = Capacitor.isNativePlatform();
  const plataforma = Capacitor.getPlatform();
  const loja = textosDaLoja(plataforma);
  // useUser devolve null fora do provider (mesma defesa do Sliders).
  const { user, currentUser } = useUser() || {};

  const [estado, setEstado] = useState('carregando'); // carregando | pronto | erro
  const [planos, setPlanos] = useState(null);
  const [tentativa, setTentativa] = useState(0);
  const [escolhido, setEscolhido] = useState('anual'); // anual | mensal
  const [modoAnual, setModoAnual] = useState('vista'); // vista | parcelado
  const [comprando, setComprando] = useState(false);
  const [erro, setErro] = useState(null);
  const [restaurando, setRestaurando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [abrindoSite, setAbrindoSite] = useState(false);
  // Código de criador validado (ou o da conta). Vai como affiliate_code.
  const [codigoCriador, setCodigoCriador] = useState('');

  // Uma vez por abertura do paywall.
  useEffect(() => {
    registrarMetrica('PLANOS_VISTOS');
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') console.log('[PLANOS] aberto', { origem, plataforma });
  }, [origem, plataforma]);

  // Carrega da loja (nativo) ou do checkout web. Spinner nunca é eterno:
  // 15s sem resposta vira erro com "Tentar de novo".
  useEffect(() => {
    let vivo = true;
    setEstado('carregando');
    const limite = setTimeout(() => {
      if (vivo) setEstado((e) => (e === 'carregando' ? 'erro' : e));
    }, TEMPO_LIMITE_MS);

    const carregar = nativo
      ? carregarPlanosDaLoja(plataforma)
      : carregarPlanoWeb().then((web) => ({ web }));

    carregar
      .then((p) => {
        if (!vivo) return;
        setPlanos(p);
        if (nativo && !p.anual && !p.parcelado) setEscolhido('mensal');
        if (nativo && !p.anual && p.parcelado) setModoAnual('parcelado');
        setEstado('pronto');
      })
      .catch((e) => {
        console.error('[PLANOS] Erro ao carregar planos', e);
        if (vivo) setEstado('erro');
      })
      .finally(() => clearTimeout(limite));

    return () => {
      vivo = false;
      clearTimeout(limite);
    };
  }, [nativo, plataforma, tentativa]);

  // ── Derivados ──────────────────────────────────────────────
  const anualAtual = useMemo(() => {
    if (!planos || !nativo) return null;
    if (modoAnual === 'parcelado' && planos.parcelado) return planos.parcelado;
    return planos.anual || planos.parcelado || null;
  }, [planos, nativo, modoAnual]);

  const mensal = nativo ? planos?.mensal || null : null;
  const plano = escolhido === 'mensal' ? mensal : anualAtual;
  const temSegmentado = !!(planos?.anual && planos?.parcelado);
  const testeDoTitulo = anualAtual?.teste || mensal?.teste || null;

  const economia =
    anualAtual && mensal && anualAtual.moeda === mensal.moeda
      ? economiaPercentual(anualAtual.total, mensal.porMes)
      : null;

  const indisponivel = nativo && lojaIndisponivel();

  // ── Ações ──────────────────────────────────────────────────
  const escolher = (qual) => {
    if (qual !== escolhido) haptics.selection();
    setEscolhido(qual);
    setErro(null);
  };

  const escolherModo = (modo) => {
    if (modo !== modoAnual || escolhido !== 'anual') haptics.selection();
    setModoAnual(modo);
    setEscolhido('anual');
    setErro(null);
  };

  const comprar = useCallback(async () => {
    if (comprando || !plano) return;
    if (lojaIndisponivel()) {
      setErro('Pagamento indisponível no momento. Tente novamente em instantes.');
      return;
    }
    haptics.light();
    setErro(null);
    setComprando(true);
    registrarMetrica('COMPRA_INICIADA');
    // Funil do criador: "chegou ao checkout" pela loja (o backend só conta indicados).
    registrarEventoCriador(auth.currentUser, 'checkout_app', 'loja');

    try {
      const uid = currentUser?.uid || auth.currentUser?.uid;
      // Afiliado da conta (travado) ou o código validado na tela: vira o
      // atributo affiliate_code ANTES da compra (como o SubscribeButton faz).
      const codigoAfiliado = codigoCriador ||
        (user?.AFFILIATE_APPLIED && user?.AFFILIATE_CODE ? String(user.AFFILIATE_CODE).toUpperCase() : '');
      const customerInfo = await comprarPacote(plano.pacote, { uid, codigoAfiliado });
      const emTeste = estaEmTeste(customerInfo);
      registrarMetrica('COMPRA_CONCLUIDA');
      if (emTeste) registrarMetrica('COMPRA_TRIAL');
      haptics.success();
      onComprado?.({ pacote: plano.pacote, customerInfo, emTeste });
    } catch (e) {
      if (compraCancelada(e)) {
        registrarMetrica('COMPRA_CANCELADA');
      } else {
        console.error('[PLANOS] Erro na compra', e);
        haptics.error();
        // Erro do SDK (tem `code`) vira mensagem genérica; os nossos já vêm em pt-BR.
        setErro(e?.code != null ? ERRO_GENERICO : e?.message || ERRO_GENERICO);
      }
    } finally {
      setComprando(false);
    }
  }, [comprando, plano, currentUser, user, onComprado]);

  const irParaSite = () => {
    haptics.light();
    onFechar?.();
    // Sem useNavigate: o funil é montado pelo MonetizacaoHost, no AppProviders, FORA do <Router>
    // (hook lá dentro lança e deixa a tela branca). /assinar é rota do BrowserRouter: carga completa serve.
    window.location.assign('/assinar');
  };

  const restaurar = async () => {
    if (restaurando) return;
    haptics.light();
    setRestaurando(true);
    setAviso(null);
    try {
      const customerInfo = await restaurarCompras();
      if (temAssinaturaAtiva(customerInfo)) {
        haptics.success();
        onComprado?.({ pacote: null, customerInfo, emTeste: estaEmTeste(customerInfo), restaurado: true });
      } else {
        setAviso('Nenhuma assinatura ativa encontrada nesta conta da loja.');
      }
    } catch (e) {
      console.warn('[PLANOS] Restaurar falhou', e);
      setAviso('Não foi possível restaurar agora. Tente novamente.');
    } finally {
      setRestaurando(false);
    }
  };

  const abrirSiteExterno = async () => {
    if (abrindoSite || !linkExterno?.onAbrir) return;
    haptics.light();
    setAbrindoSite(true);
    try {
      await linkExterno.onAbrir();
    } catch (e) {
      console.warn('[PLANOS] Link externo falhou', e);
    } finally {
      setAbrindoSite(false);
    }
  };

  const abrirUrl = (url) => {
    haptics.light();
    window.open(url, '_blank');
  };

  // ── Partes ─────────────────────────────────────────────────
  const links = (
    <div className={s.links}>
      {nativo && (
        <>
          <button type="button" className={s.linkPequeno} onClick={restaurar} disabled={restaurando}>
            {restaurando ? 'Restaurando…' : 'Restaurar compras'}
          </button>
          <span className={s.ponto} aria-hidden="true">·</span>
        </>
      )}
      <button type="button" className={s.linkPequeno} onClick={() => abrirUrl(URL_TERMOS)}>
        Termos de Uso
      </button>
      <span className={s.ponto} aria-hidden="true">·</span>
      <button type="button" className={s.linkPequeno} onClick={() => abrirUrl(URL_PRIVACIDADE)}>
        Política de Privacidade
      </button>
    </div>
  );

  const cabecalho = (
    <div className={s.cabeca}>
      <h1 className={s.titulo}>
        Escolha seu plano.
        {testeDoTitulo && <span className={s.tituloDestaque}>Grátis por {testeDoTitulo.rotulo}.</span>}
      </h1>
    </div>
  );

  const renderCardAnual = () => {
    if (!anualAtual) return null;
    const ativo = escolhido === 'anual';
    const parcelado = anualAtual.tipo === 'parcelado';
    const rotulo = parcelado
      ? `Anual, ${anualAtual.parcelas}x de ${anualAtual.parcelaTexto}, ${anualAtual.totalTexto} no total`
      : `Anual, ${anualAtual.totalTexto} por ano, equivale a ${anualAtual.porMesTexto} por mês`;
    return (
      <div className={cx(s.card, ativo && s.cardAtivo)}>
        <button
          type="button"
          role="radio"
          aria-checked={ativo}
          aria-label={rotulo}
          className={s.alvo}
          onClick={() => escolher('anual')}
        />
        {ativo && (
          <span className={s.canto} aria-hidden="true">
            <Check tamanho={18} />
          </span>
        )}
        <div className={cx(s.cardTopo, s.cardTopoComCanto)} aria-hidden="true">
          <div className={s.cardNomes}>
            <span className={s.cardNome}>
              Anual
              {economia && <span className={s.badge}>Economize {economia}%</span>}
            </span>
            <span className={s.cardSub}>
              {parcelado ? `${anualAtual.totalTexto} no total` : `${anualAtual.totalTexto}/ano`}
            </span>
          </div>
          <span className={s.cardPreco}>{anualAtual.porMesTexto}/mês</span>
        </div>

        {temSegmentado && (
          <div className={s.segmentado} role="radiogroup" aria-label="Forma de pagamento do anual">
            <button
              type="button"
              role="radio"
              aria-checked={modoAnual === 'vista'}
              className={cx(s.segmento, modoAnual === 'vista' && s.segmentoAtivo)}
              onClick={() => escolherModo('vista')}
            >
              À vista · {planos.anual.totalTexto}
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={modoAnual === 'parcelado'}
              className={cx(s.segmento, modoAnual === 'parcelado' && s.segmentoAtivo)}
              onClick={() => escolherModo('parcelado')}
            >
              {planos.parcelado.parcelas}x de {planos.parcelado.parcelaTexto}
            </button>
          </div>
        )}

        <div className={s.cardBase} aria-hidden="true">
          <ul className={cx(s.beneficios, ativo && s.beneficiosAtivos)}>
            {anualAtual.teste && <Beneficio>{anualAtual.teste.rotulo} grátis pra testar</Beneficio>}
            {PREMIUM_SEM_ANUNCIOS && <Beneficio>Sem anúncios</Beneficio>}
            {!(temSegmentado && anualAtual.teste && PREMIUM_SEM_ANUNCIOS) && (
              <Beneficio>Tudo do Turbo liberado</Beneficio>
            )}
          </ul>
          <img className={cx(s.coroa, temSegmentado && s.coroaMenor, !ativo && s.coroaApagada)} src={vitaCoroa} alt="" />
        </div>
      </div>
    );
  };

  const renderCardMensal = () => {
    if (!mensal) return null;
    const ativo = escolhido === 'mensal';
    // Com o segmentado do parcelado o card Anual cresce: o Mensal fica compacto.
    const compacto = temSegmentado;
    return (
      <div className={cx(s.card, ativo && s.cardAtivo)}>
        <button
          type="button"
          role="radio"
          aria-checked={ativo}
          aria-label={`Mensal, ${mensal.totalTexto} por mês${mensal.teste ? '' : ', sem teste grátis'}`}
          className={s.alvo}
          onClick={() => escolher('mensal')}
        />
        {ativo && (
          <span className={s.canto} aria-hidden="true">
            <Check tamanho={18} />
          </span>
        )}
        <div className={cx(s.cardTopo, ativo && s.cardTopoComCanto)} aria-hidden="true">
          <div className={s.cardNomes}>
            <span className={s.cardNome}>Mensal</span>
            <span className={s.cardSub}>
              {compacto && !mensal.teste ? 'Cobrado todo mês · sem teste grátis' : 'Cobrado todo mês'}
            </span>
          </div>
          <span className={s.cardPreco}>{mensal.totalTexto}/mês</span>
        </div>
        {!compacto && (
          <ul className={cx(s.beneficios, ativo && s.beneficiosAtivos)} aria-hidden="true">
            {mensal.teste && <Beneficio>{mensal.teste.rotulo} grátis pra testar</Beneficio>}
            {PREMIUM_SEM_ANUNCIOS && <Beneficio>Sem anúncios</Beneficio>}
            <Beneficio>Tudo do Turbo liberado</Beneficio>
            {!mensal.teste && <Beneficio sem>Sem teste grátis</Beneficio>}
          </ul>
        )}
      </div>
    );
  };

  const renderCardWeb = () => {
    const web = planos?.web;
    if (!web) return null;
    return (
      <div className={cx(s.card, s.cardAtivo)}>
        <span className={s.canto} aria-hidden="true">
          <Check tamanho={18} />
        </span>
        <div className={cx(s.cardTopo, s.cardTopoComCanto)}>
          <div className={s.cardNomes}>
            <span className={s.cardNome}>Anual</span>
            <span className={s.cardSub}>{web.totalTexto}/ano</span>
          </div>
          <span className={s.cardPreco}>
            {web.parcelas}x de {web.parcelaTexto}
          </span>
        </div>
        <div className={s.cardBase}>
          <ul className={cx(s.beneficios, s.beneficiosAtivos)}>
            {PREMIUM_SEM_ANUNCIOS && <Beneficio>Sem anúncios</Beneficio>}
            <Beneficio>Tudo do Turbo liberado</Beneficio>
          </ul>
          <img className={s.coroa} src={vitaCoroa} alt="" />
        </div>
      </div>
    );
  };

  // ── Estados de carga ───────────────────────────────────────
  let corpo;
  if (estado === 'carregando') {
    corpo = (
      <div className={s.centro}>
        <Loader tamanho={64} aria-label="Carregando planos" />
      </div>
    );
  } else if (estado === 'erro') {
    corpo = (
      <div className={s.centro} role="alert">
        <p className={s.erroTexto}>Não foi possível carregar os planos. Verifique sua conexão e tente novamente.</p>
        <button
          type="button"
          className={s.botaoSecundario}
          onClick={() => {
            haptics.light();
            setTentativa((n) => n + 1);
          }}
        >
          Tentar de novo
        </button>
        {links}
        {aviso && <p className={s.aviso}>{aviso}</p>}
      </div>
    );
  } else {
    corpo = (
      <div className={s.lista} role={nativo ? 'radiogroup' : undefined} aria-label={nativo ? 'Planos' : undefined}>
        {nativo ? (
          <>
            {renderCardAnual()}
            {renderCardMensal()}
          </>
        ) : (
          renderCardWeb()
        )}
      </div>
    );
  }

  let barra = null;
  if (estado === 'pronto' && nativo) {
    const rotuloCta = indisponivel
      ? 'Pagamento indisponível'
      : plano?.teste
        ? `Experimente por ${formatarPreco(0, plano.moeda)}`
        : escolhido === 'mensal'
          ? 'Assinar mensal'
          : 'Assinar anual';
    barra = (
      <div className={s.barra}>
        <p className={s.cancele}>Cancele quando quiser {loja.naLoja}.</p>
        <button
          type="button"
          className={s.cta}
          onClick={comprar}
          disabled={!plano || indisponivel || comprando}
          aria-busy={comprando || undefined}
        >
          {comprando && <span className={s.spinner} aria-hidden="true" />}
          {rotuloCta}
        </button>
        {(erro || indisponivel) && (
          <p className={s.erroCompra} role="alert">
            {erro || 'Pagamento indisponível no momento. Tente novamente em instantes.'}
          </p>
        )}
        {linkExterno?.ativo && (
          <div className={s.externo}>
            <button
              type="button"
              className={s.botaoExterno}
              onClick={abrirSiteExterno}
              disabled={abrindoSite}
              aria-busy={abrindoSite || undefined}
            >
              Assinar pelo site
            </button>
            <span className={s.notaExterna}>Você será levado a um site externo.</span>
          </div>
        )}
        <p className={s.legal}>{textoLegal(plano, loja)}</p>
        {links}
        {aviso && <p className={s.aviso}>{aviso}</p>}
      </div>
    );
  } else if (estado === 'pronto') {
    barra = (
      <div className={s.barra}>
        <p className={s.cancele}>Pagamento no cartão ou Pix, no site da LesVita.</p>
        <button type="button" className={s.cta} onClick={irParaSite}>
          Continuar para o pagamento
        </button>
        <p className={s.legal}>
          Plano anual sem renovação automática: você renova quando quiser. 7 dias de garantia com reembolso integral.
        </p>
        {links}
      </div>
    );
  }

  return (
    <PainelPremium rotulo="Escolha seu plano" brilho="topo" onFechar={onFechar} onVoltar={onVoltar}>
      <PremiumTopBar onFechar={onFechar} onVoltar={onVoltar} />
      <div className={s.corpo}>
        {cabecalho}
        {corpo}
        {estado === 'pronto' && nativo && (
          <CodigoCriador
            codigoDaConta={user?.AFFILIATE_APPLIED && user?.AFFILIATE_CODE
              ? String(user.AFFILIATE_CODE).toUpperCase() : ''}
            onMudar={setCodigoCriador}
          />
        )}
      </div>
      {barra}
    </PainelPremium>
  );
}
